import { Role, rolesManipulator, useGetAccessTokenPayload } from 'auth';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { BUSINESS_QUERY_PARAM } from '@/modules/common/constants/search-params.ts';
import { CURRENT_FORM_QUERY_KEY } from '@/modules/common/queries/forms.ts';
import {
  INCIDENTS_LIST_QUERY_KEY,
  INCIDENTS_TYPES_LIST_QUERY_KEY,
} from '@/modules/common/queries/incidents.ts';
import {
  PUBLIC_REGISTERS_LIST_QUERY_KEY,
  REGISTERS_LIST_QUERY_KEY,
} from '@/modules/common/queries/registers.ts';
import { REPORTS_LIST_QUERY_KEY } from '@/modules/common/queries/reports.ts';
import {
  CURRENT_USER_PUBLIC_TASK_QUERY_KEY,
  CURRENT_USER_TASKS_GRAPHIC_QUERY_KEY,
  CURRENT_USER_TASKS_LIST_BY_LINKED_INCIDENT_QUERY_KEY,
  CURRENT_USER_TASKS_LIST_QUERY_KEY,
  CURRENT_USER_TASKS_MONTH_STATISTIC_QUERY_KEY,
  CURRENT_USER_TASK_QUERY_KEY,
} from '@/modules/common/queries/tasks.ts';
import { queryClient } from '@/modules/common/query.ts';
import {
  CUSTOM_CHARTS_DATA_QUERY_KEY,
  CUSTOM_CHARTS_DETAIL_QUERY_KEY,
  CUSTOM_CHARTS_FORM_FIELDS_QUERY_KEY,
  CUSTOM_CHARTS_LIST_QUERY_KEY,
} from '@/modules/admin-dashboard/queries/custom-charts.ts';
import {
  CURRENT_QUERY_DEPARTMENT_QUERY_KEY,
  CURRENT_USER_MY_DEPARTMENTS_QUERY_KEY,
} from '@/modules/admin-dashboard/queries/department.ts';
import { DOCUMENTS_TREE_QK } from '@/modules/admin-dashboard/queries/documents-folders.ts';
import { DOCUMENTS_LIST_QUERY_KEY } from '@/modules/admin-dashboard/queries/documents.ts';
import { TAGS_LIST_QK } from '@/modules/admin-dashboard/queries/form-tags.ts';
import {
  CURRENT_USER_FORMS_ITEMS_LIST_QUERY_KEY,
  CURRENT_USER_FORMS_LIST_QUERY_KEY,
} from '@/modules/admin-dashboard/queries/forms.ts';
import {
  CURRENT_REPORTS_BATCH_QUERY_KEY,
  REPORTS_BATCH_LIST_QUERY_KEY,
  REPORT_BATCH_ZIP_ARCHIVE_QUERY_KEY,
} from '@/modules/admin-dashboard/queries/reports-batch.ts';
import {
  REPORTS_TEMPLATES_LIST_QUERY_KEY,
  REPORT_TEMPLATE_QUERY_KEY,
} from '@/modules/admin-dashboard/queries/reports-templates.ts';
import {
  CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY,
  CURRENT_USER_UPCOMING_TASKS_CHART_QUERY_KEY,
  TASKS_RESULTS_QUERY_KEY,
} from '@/modules/admin-dashboard/queries/tasks.ts';
import { USERS_LIST_QUERY_KEY } from '@/modules/admin-dashboard/queries/users.ts';
import { PageLayout, Spinner } from 'ui';

const AI_CHAT_URL = import.meta.env.VITE_COMPLIANCE_AI_CHAT_URL;

const AIChat = () => {
  const [loading, setLoading] = useState(true);
  const { refreshToken } = useGetAccessTokenPayload();
  const [token] = useState<string>(refreshToken ?? '');
  const [params] = useSearchParams();
  const bussinesId = params.get(BUSINESS_QUERY_PARAM);
  const userRole = rolesManipulator.current_user_role as Role;

  useEffect(() => {
    const streamlitOrigin = new URL(AI_CHAT_URL).origin;

    const handleMessage = (event: MessageEvent<{ type?: string }>) => {
      if (event.origin !== streamlitOrigin || event.data?.type !== 'REQUEST_TOKEN') return;

      (event.source as WindowProxy | null)?.postMessage(
        { type: 'TOKEN_RESPONSE', token },
        streamlitOrigin,
      );
    };

    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, [token]);

  useEffect(() => {
    return () => {
      queryClient.invalidateQueries({
        queryKey: [INCIDENTS_TYPES_LIST_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [INCIDENTS_LIST_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [REPORTS_LIST_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CURRENT_USER_TASKS_GRAPHIC_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CURRENT_USER_TASKS_MONTH_STATISTIC_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CURRENT_USER_TASKS_LIST_BY_LINKED_INCIDENT_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CURRENT_USER_TASK_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CURRENT_USER_PUBLIC_TASK_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [PUBLIC_REGISTERS_LIST_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CURRENT_USER_TASKS_LIST_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [REGISTERS_LIST_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CURRENT_USER_FORMS_LIST_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CURRENT_FORM_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CUSTOM_CHARTS_LIST_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CUSTOM_CHARTS_DETAIL_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CUSTOM_CHARTS_DATA_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CUSTOM_CHARTS_FORM_FIELDS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CURRENT_USER_MY_DEPARTMENTS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CURRENT_QUERY_DEPARTMENT_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [DOCUMENTS_LIST_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [DOCUMENTS_TREE_QK],
      });
      queryClient.invalidateQueries({
        queryKey: [TAGS_LIST_QK],
      });
      queryClient.invalidateQueries({
        queryKey: [CURRENT_USER_FORMS_LIST_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CURRENT_USER_FORMS_ITEMS_LIST_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [REPORTS_BATCH_LIST_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [REPORT_BATCH_ZIP_ARCHIVE_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CURRENT_REPORTS_BATCH_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [REPORT_TEMPLATE_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [REPORTS_TEMPLATES_LIST_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CURRENT_USER_UPCOMING_TASKS_CHART_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [CURRENT_USER_TASKS_ACTIVITY_LOGS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [TASKS_RESULTS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [USERS_LIST_QUERY_KEY],
      });
    };
  }, []);

  return (
    <PageLayout
      title={
        <span className="flex items-center gap-3">
          AI Chat <BetaBadge />
        </span>
      }
    >
      <div className="flex flex-col gap-6 w-full min-h-full">
        {loading && (
          <div className="w-full h-full flex items-center justify-center">
            <Spinner size="lg" />
          </div>
        )}

        <iframe
          className="w-full h-full overflow-hidden"
          src={`${AI_CHAT_URL}?role=${userRole}&business_id=${bussinesId}`}
          allow="camera; microphone; clipboard-write; geolocation"
          onLoad={() => setLoading(false)}
        ></iframe>
      </div>
    </PageLayout>
  );
};

export const BetaBadge = () => {
  return (
    <span
      className="px-2.5 py-0.5 text-xs font-[Inter] font-medium rounded-full text-white shadow-md"
      style={{
        background: 'linear-gradient(90deg, #1E5984, #24699F, #2E84C5, #3DA4E7)',
      }}
    >
      Beta
    </span>
  );
};

export default withPrivateAccess(AIChat, [Role.COMPLIANCE_USER, Role.COMPLIANCE_ADMIN]);
